import { Link, Navigate, useParams } from "react-router-dom";
import { useFetchItemsByRestaurantQuery, useTypedSelector } from "../store";
import InfoChip from "../components/common/InfoChip";
import { Item, MenuHeader, Severity } from "../types";
import { ReactNode, useState } from "react";
import ItemDetails from "../components/items/ItemDetails";
import { IoMdArrowRoundBack } from "react-icons/io";
import classNames from "classnames";
import { IoCartOutline } from "react-icons/io5";
import { formatPrice } from "../utils/helpers";
import Button from "../components/common/Button";
import Skeleton from "../components/common/Skeleton";

const RestaurantDetailsWrapper = () => {
  const { restaurantId } = useParams();

  if (!restaurantId) return <Navigate to="/restaurants" />;

  return <RestaurantDetails restaurantId={restaurantId} />;
};

const RestaurantDetails: React.FC<{
  restaurantId: string;
}> = ({ restaurantId }) => {
  const [selectedItemId, setSelectedItemId] = useState<string | undefined>();
  const {
    data: restaurantItems,
    error,
    isFetching,
  } = useFetchItemsByRestaurantQuery(restaurantId);

  const { items } = useTypedSelector((state) => state.cart);

  const cartItemsTotalPrice = items.reduce((acc, item) => {
    return acc + item.totalPrice * item.qty;
  }, 0);

  const totalQty = items.reduce((acc, item) => acc + item.qty, 0);

  let content: ReactNode;

  if (error) {
    content = (
      <InfoChip
        severity={Severity.DANGER}
        content="Restaurant Introuvable"
      />
    );
  } else if (isFetching) {
    content = (
      <div className="flex flex-col w-full max-w-5xl mx-auto">
        <Skeleton
          times={1}
          className="w-full h-[200px] md:h-[300px]"
        />
        <div className="w-full px-4 flex flex-col mx-auto -mt-12 relative z-10">
          <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100">
            <Skeleton
              times={1}
              className="w-48 h-8 mb-2"
            />
            <Skeleton
              times={1}
              className="w-3/4 h-4"
            />
          </div>
          <div className="flex gap-2 mt-6 overflow-x-hidden">
            <Skeleton
              times={4}
              className="w-24 h-8 rounded-full"
            />
          </div>
          <div className="mt-8 space-y-8">
            <Skeleton
              times={3}
              className="w-full h-32 rounded-lg"
            />
          </div>
        </div>
      </div>
    );
  } else if (restaurantItems) {
    const { restaurant, headers } = restaurantItems;

    const renderedHeaders = headers.map((header) => {
      return (
        <HeaderChip
          key={header.headerName}
          headerName={header.headerName}
        />
      );
    });

    const renderedHeaderItems = headers.map((header) => {
      return (
        <HeaderSection
          key={header.headerName}
          header={header}
          onSelectItem={setSelectedItemId}
        />
      );
    });

    content = (
      <div className="flex flex-col w-full bg-white min-h-screen pb-24">
        {/* Hero Section */}
        <div className="relative w-full h-[200px] md:h-[300px]">
          <img
            src={restaurant.images.bannerPath}
            alt={restaurant.displayName}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 to-transparent pointer-events-none" />
          <Link to="/restaurants">
            <div className="absolute left-4 top-4 bg-white/90 p-2 rounded-full hover:bg-white transition-colors shadow-sm cursor-pointer z-20">
              <IoMdArrowRoundBack className="w-6 h-6 text-gray-800" />
            </div>
          </Link>
        </div>

        {/* Restaurant Info Card */}
        <div className="w-full max-w-5xl mx-auto px-4 -mt-10 relative z-10 mb-6">
          <div className="bg-white p-5 rounded-xl shadow-md border border-gray-100">
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
              {restaurant.displayName}
            </h1>
            <p className="text-gray-500 font-light text-sm md:text-base leading-relaxed">
              {restaurant.description}
            </p>
          </div>
        </div>

        {/* Sticky Category Nav */}
        <div className="sticky top-0 z-30 bg-white border-b border-gray-100 shadow-sm py-3 px-4 mb-4 overflow-x-auto whitespace-nowrap no-scrollbar">
          <div className="flex gap-3 max-w-5xl mx-auto">{renderedHeaders}</div>
        </div>

        {/* Menu Items */}
        <div className="w-full max-w-5xl mx-auto px-4 flex flex-col gap-8">
          {renderedHeaderItems}
        </div>

        {/* Floating Cart Button */}
        {totalQty > 0 && (
          <div className="fixed bottom-6 left-0 right-0 z-40 px-4 flex justify-center pointer-events-none">
            <Link
              to="/checkout"
              className="w-full max-w-md pointer-events-auto shadow-xl hover:shadow-2xl transition-shadow rounded-full"
            >
              <Button className="w-full bg-black text-white py-4 px-6 rounded-full font-semibold text-lg flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="bg-white/20 px-3 py-1 rounded-full text-sm font-bold">
                    {totalQty}
                  </span>
                  <span>Voir le panier</span>
                </div>
                <span className="font-bold">
                  {formatPrice(cartItemsTotalPrice)}
                </span>
              </Button>
            </Link>
          </div>
        )}
      </div>
    );
  }
  return (
    <div className="w-full flex h-full flex-col items-center justify-center bg-gray-50">
      {content}
      {selectedItemId && (
        <ItemDetails
          itemId={selectedItemId}
          onClose={() => setSelectedItemId(undefined)}
        />
      )}
    </div>
  );
};

export default RestaurantDetailsWrapper;

const HeaderChip: React.FC<{ headerName: string }> = ({ headerName }) => {
  const handleClick = () => {
    const section = document.getElementById(headerName);
    if (section) {
      // Offset for sticky header
      const yOffset = -80;
      const y =
        section.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: "smooth" });
    }
  };
  return (
    <button
      className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-full text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-gray-300"
      onClick={handleClick}
    >
      {headerName}
    </button>
  );
};

const HeaderSection: React.FC<{
  header: MenuHeader;
  onSelectItem: (itemId: string) => void;
}> = ({ header, onSelectItem }) => {
  const { headerName, items } = header;
  const renderedItems = items.map((item) => {
    return (
      <ItemCard
        key={item._id}
        item={item}
        onSelect={onSelectItem}
      />
    );
  });
  return (
    <div
      id={headerName}
      className="w-full flex flex-col gap-4"
    >
      <h2 className="text-xl md:text-2xl font-bold text-gray-800 border-l-4 border-primary pl-3">
        {headerName}
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {renderedItems}
      </div>
    </div>
  );
};

const ItemCard: React.FC<{
  item: Item;
  onSelect: (itemId: string) => void;
}> = ({ item, onSelect }) => {
  const { _id, displayName, description, imagePath, price } = item;

  return (
    <div
      className="flex justify-between p-4 bg-white border border-gray-100 rounded-xl hover:border-gray-300 hover:shadow-md transition-all cursor-pointer h-full"
      onClick={() => onSelect(_id)}
    >
      <div className="flex flex-col justify-between flex-1 pr-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-1 line-clamp-2">
            {displayName}
          </h3>
          {description && (
            <p className="text-gray-500 text-sm line-clamp-2 mb-2">
              {description}
            </p>
          )}
        </div>
        <div className="font-medium text-gray-900 mt-2">{price.text}</div>
      </div>

      {imagePath && (
        <div className="w-[100px] h-[100px] md:w-[120px] md:h-[120px] flex-shrink-0">
          <img
            src={imagePath}
            alt={displayName}
            className="w-full h-full object-cover rounded-lg bg-gray-50"
          />
        </div>
      )}
    </div>
  );
};
